import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, ButtonBase, Divider, IconButton, InputAdornment,
    MenuItem, Stack, Switch, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { ConfigCard } from '../style/cards';
import { CheckboxTile, PdiRow } from '../style/cards/_PdiHelpers';

// ────────────── Static option pools ──────────────
//
// In a real wiring these come from the inventory backend; for now they
// stay inline so the UI is browseable.
const WEBSHOP_INSTANCES = ['Online 1', 'Online 2', 'Online 3', 'Online 4', 'Online 5'];
const STORES = [
    { code: '01', name: 'Lochristi'    },
    { code: '02', name: 'Charleroi'    },
    { code: '03', name: 'Destelbergen' },
    { code: '04', name: 'softtouch'    },
    { code: '05', name: 'Kuurne'       },
    { code: '06', name: 'Gavere'       },
    { code: '07', name: 'SDW'          },
    { code: '08', name: 'Ternat'       },
    { code: '98', name: 'Ontvangst'    },
];
const SOURCE_FIELDS = [
    'Source field — Categorie 1 (Gender)',
    'Source field — Categorie 2 (Product type)',
    'Source field — Categorie 3 (Subtype)',
    'Source field — Categorie 4 (Brand)',
    'Source field — Categorie 5 (Collection)',
    'Source field — Categorie 6 (Material)',
    'Source field — Categorie 7 (Style)',
];
const PRODUCT_NAME_FIELDS    = ['Source field — Referentie 1', 'Source field — Referentie 2', 'Source field — Naam'];
const PRODUCT_REF_FIELDS     = ['Source field — Referentie 1', 'Source field — Referentie 2', 'Source field — SKU'];
const LANG_SLOTS             = ['Lang 1', 'Lang 2', 'Lang 3', 'Lang 4', 'Lang 5'];
const IMAGE_TYPES            = ['Type 01', 'Type 02', 'Type 03', 'Type 04'];

const SyncSettingsTab = ({ theme, t, subTab = 0 }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

    // ────────────── Big config object ──────────────
    const [config, setConfig] = useState({
        // GENERAL
        sourceWebshop: 'Online 2',
        storesIncluded: ['01', '02', '03', '04'],
        stockFiltering: {
            onlySyncWithStock:        true,
            useExternalStockProvider: false,
            showExternalOnlyProducts: false,
        },
        // FIELD MAPPING
        categoryHierarchy: [
            { id: 'c1', source: 'Source field — Categorie 1 (Gender)' },
            { id: 'c2', source: 'Source field — Categorie 2 (Product type)' },
            { id: 'c3', source: 'Source field — Categorie 3 (Subtype)' },
            { id: 'c4', source: 'Source field — Categorie 4 (Brand)' },
        ],
        productFields: {
            name:      'Source field — Referentie 2',
            reference: 'Source field — Referentie 1',
        },
        languageMapping: {
            nl: 'Lang 1',
            en: 'Lang 2',
            fr: 'Lang 3',
        },
        // IMAGES
        imageSourceTypes: {
            primary:     'Type 01',
            alternative: 'Type 02',
        },
        imageProcessing: {
            useV2:    true,
            sizes: {
                thumb:     { width: 200, height: 320  },
                preview:   { width: 750, height: 1120 },
                processed: { width: 800, height: 1300 },
            },
            backgroundFillColor: '#ffffff',
        },
    });
    const setSection = (k) => (v) => setConfig(prev => ({ ...prev, [k]: { ...(prev[k] || {}), ...v } }));

    // Renders only the cards for the active sub-tab. The sub-tab UI itself
    // lives in WebshopConfigScreen so it sits flush under the main tabs
    // (same pattern as the Pages and Style tabs).
    return (
        <Box>
            {subTab === 0 && (
                <Stack spacing={2}>
                    <SourceWebshopCard
                        value={config.sourceWebshop}
                        onChange={(v) => setConfig(p => ({ ...p, sourceWebshop: v }))}
                        theme={theme} tr={tr}
                    />
                    <StoresIncludedCard
                        value={config.storesIncluded}
                        onChange={(v) => setConfig(p => ({ ...p, storesIncluded: v }))}
                        theme={theme} tr={tr}
                    />
                    <StockFilteringCard
                        value={config.stockFiltering}
                        onChange={setSection('stockFiltering')}
                        theme={theme} tr={tr}
                    />
                </Stack>
            )}

            {subTab === 1 && (
                <Stack spacing={2}>
                    <CategoryHierarchyCard
                        value={config.categoryHierarchy}
                        onChange={(v) => setConfig(p => ({ ...p, categoryHierarchy: v }))}
                        theme={theme} tr={tr}
                    />
                    <ProductFieldsCard
                        value={config.productFields}
                        onChange={setSection('productFields')}
                        theme={theme} tr={tr}
                    />
                    <LanguageMappingCard
                        value={config.languageMapping}
                        onChange={setSection('languageMapping')}
                        theme={theme} tr={tr}
                    />
                </Stack>
            )}

            {subTab === 2 && (
                <Stack spacing={2}>
                    <ImageSourceTypesCard
                        value={config.imageSourceTypes}
                        onChange={setSection('imageSourceTypes')}
                        theme={theme} tr={tr}
                    />
                    <ImageProcessingCard
                        value={config.imageProcessing}
                        onChange={setSection('imageProcessing')}
                        theme={theme} tr={tr}
                    />
                </Stack>
            )}
        </Box>
    );
};

SyncSettingsTab.propTypes = { theme: PropTypes.string, t: PropTypes.func, subTab: PropTypes.number };

// ─────────────────────────────────────────────────────────────────────────
// GENERAL — Source webshop
// ─────────────────────────────────────────────────────────────────────────
const SourceWebshopCard = ({ value, onChange, theme, tr }) => (
    <ConfigCard
        title={tr('cfg_sync_source_title', 'Source webshop')}
        description={tr('cfg_sync_source_desc', 'Identifies which webshop instance this configuration pulls data for. The source application supports up to 5 webshops; pick which one this configuration represents.')}
        theme={theme}
    >
        <PdiRow
            label={tr('cfg_sync_source_instance', 'Webshop instance')}
            help={tr('cfg_sync_source_instance_help', 'Only products marked as online for this instance will be synced')}
            control={
                <TextField
                    select size="small"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    sx={{ minWidth: 180 }}
                >
                    {WEBSHOP_INSTANCES.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                </TextField>
            }
        />
    </ConfigCard>
);
SourceWebshopCard.propTypes = { value: PropTypes.string, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// GENERAL — Stores included
// ─────────────────────────────────────────────────────────────────────────
const StoresIncludedCard = ({ value = [], onChange, theme, tr }) => {
    const isSelected = (code) => value.includes(code);
    const toggle = (code) => onChange(isSelected(code) ? value.filter(c => c !== code) : [...value, code]);
    const selectAll = () => onChange(STORES.map(s => s.code));
    const clearAll  = () => onChange([]);

    return (
        <ConfigCard
            title={tr('cfg_sync_stores_title', 'Stores included')}
            description={tr('cfg_sync_stores_desc', 'Only products from these physical stores will be available in the webshop.')}
            theme={theme}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {value.length} {tr('cfg_sync_stores_of', 'of')} {STORES.length} {tr('cfg_sync_stores_selected', 'stores selected')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" onClick={selectAll} sx={{ textTransform: 'none' }}>
                        {tr('cfg_sync_stores_select_all', 'Select all')}
                    </Button>
                    <Button size="small" variant="outlined" onClick={clearAll} sx={{ textTransform: 'none' }}>
                        {tr('cfg_sync_stores_clear_all', 'Clear all')}
                    </Button>
                </Box>
            </Box>

            <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1 }}>
                    {STORES.map(s => (
                        <CheckboxTile
                            key={s.code}
                            label={
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.65rem' }}>{s.code}</Typography>
                                    <Typography component="span" variant="body2" sx={{ fontWeight: 'inherit', color: 'inherit' }}>{s.name}</Typography>
                                </Box>
                            }
                            checked={isSelected(s.code)}
                            onChange={() => toggle(s.code)}
                        />
                    ))}
                </Box>
            </Box>
        </ConfigCard>
    );
};
StoresIncludedCard.propTypes = { value: PropTypes.array, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// GENERAL — Stock filtering
// ─────────────────────────────────────────────────────────────────────────
const StockFilteringCard = ({ value = {}, onChange, theme, tr }) => {
    const set = (k) => (v) => onChange({ [k]: v });
    const externalOn = !!value.useExternalStockProvider;

    return (
        <ConfigCard
            title={tr('cfg_sync_stock_title', 'Stock filtering')}
            description={tr('cfg_sync_stock_desc', 'Control whether products without stock are synced to the webshop.')}
            theme={theme}
        >
            <Stack spacing={1.25} divider={<Divider />}>
                <PdiRow
                    label={tr('cfg_sync_stock_only', 'Only sync products with stock')}
                    help={tr('cfg_sync_stock_only_help', "Products with zero inventory in the selected stores won't appear in the webshop")}
                    control={
                        <Switch color="success" size="small"
                            checked={!!value.onlySyncWithStock}
                            onChange={(e) => set('onlySyncWithStock')(e.target.checked)}
                        />
                    }
                />
                <PdiRow
                    label={tr('cfg_sync_stock_external', 'Use external stock provider')}
                    help={tr('cfg_sync_stock_external_help', "When local stock is empty, check an external supplier's inventory. Only products from supported brands are eligible.")}
                    control={
                        <Switch color="success" size="small"
                            checked={externalOn}
                            onChange={(e) => set('useExternalStockProvider')(e.target.checked)}
                        />
                    }
                />
                <Box sx={{ opacity: externalOn ? 1 : 0.5, pointerEvents: externalOn ? 'auto' : 'none' }}>
                    <PdiRow
                        indent
                        label={tr('cfg_sync_stock_external_only', 'Show external-only products')}
                        help={tr('cfg_sync_stock_external_only_help', "Override the 'only sync products with stock' rule above and include products that are only available externally. Useful for showing supplier inventory you can dropship.")}
                        control={
                            <Switch color="success" size="small"
                                checked={!!value.showExternalOnlyProducts}
                                onChange={(e) => set('showExternalOnlyProducts')(e.target.checked)}
                            />
                        }
                    />
                </Box>
            </Stack>
        </ConfigCard>
    );
};
StockFilteringCard.propTypes = { value: PropTypes.object, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// FIELD MAPPING — Category hierarchy
// ─────────────────────────────────────────────────────────────────────────
const MAX_LEVELS = 7;
const CategoryHierarchyCard = ({ value = [], onChange, theme, tr }) => {
    const setLevel = (id, source) => onChange(value.map(l => l.id === id ? { ...l, source } : l));
    const remove   = (id) => onChange(value.filter(l => l.id !== id));
    const addLevel = () => {
        if (value.length >= MAX_LEVELS) return;
        onChange([...value, { id: `c_${Date.now()}`, source: '' }]);
    };

    return (
        <ConfigCard
            title={tr('cfg_sync_cat_title', 'Category hierarchy')}
            description={tr('cfg_sync_cat_desc', 'Define the category tree that drives navigation and filtering on the webshop. Each level maps to a source field, top to bottom.')}
            theme={theme}
        >
            <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1, lineHeight: 1.4 }}>
                    {tr('cfg_sync_cat_helper', 'Order matters: level 1 is the highest category (e.g. gender), level 2 sits under it (e.g. type), and so on. Maximum 7 levels.')}
                </Typography>

                <Stack spacing={0.75}>
                    {value.map((level, idx) => (
                        <Box key={level.id} sx={{
                            display: 'grid',
                            gridTemplateColumns: '28px auto 1fr 32px',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: '#fff',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            px: 1, py: 0.75,
                        }}>
                            <Box sx={{
                                width: 24, height: 24, borderRadius: '50%',
                                backgroundColor: 'rgba(25,118,210,0.08)',
                                color: 'primary.main',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontWeight: 700, fontSize: '0.78rem',
                            }}>{idx + 1}</Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 80 }}>
                                {idx === 0
                                    ? tr('cfg_sync_cat_top_level', 'Top level')
                                    : tr('cfg_sync_cat_sub_level', 'Sub-level')}
                            </Typography>
                            <TextField
                                select size="small" fullWidth
                                value={level.source}
                                onChange={(e) => setLevel(level.id, e.target.value)}
                                sx={{ '& .MuiInputBase-input': { py: 0.5, fontSize: '0.85rem' } }}
                            >
                                <MenuItem value="">— {tr('cfg_sync_cat_select_source', 'select source field')} —</MenuItem>
                                {SOURCE_FIELDS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                            </TextField>
                            <IconButton size="small" onClick={() => remove(level.id)} sx={{ color: 'text.disabled' }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}

                    {value.length < MAX_LEVELS && (
                        <ButtonBase
                            onClick={addLevel}
                            sx={{
                                width: '100%', py: 1,
                                border: '1.5px dashed',
                                borderColor: 'divider',
                                borderRadius: 1,
                                backgroundColor: '#fff',
                                color: 'text.secondary',
                                gap: 0.5,
                                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                            }}
                        >
                            <AddIcon fontSize="small" />
                            <Typography variant="body2">
                                {tr('cfg_sync_cat_add', 'Add another level')}
                            </Typography>
                        </ButtonBase>
                    )}
                </Stack>
            </Box>
        </ConfigCard>
    );
};
CategoryHierarchyCard.propTypes = { value: PropTypes.array, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// FIELD MAPPING — Product fields
// ─────────────────────────────────────────────────────────────────────────
const ProductFieldsCard = ({ value = {}, onChange, theme, tr }) => (
    <ConfigCard
        title={tr('cfg_sync_pfields_title', 'Product fields')}
        description={tr('cfg_sync_pfields_desc', 'Pick which source fields hold the product name and reference (SKU).')}
        theme={theme}
    >
        <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <UpperLabelSelect
                    label={tr('cfg_sync_pfields_name', 'PRODUCT NAME')}
                    value={value.name || ''}
                    options={PRODUCT_NAME_FIELDS}
                    onChange={(v) => onChange({ name: v })}
                />
                <UpperLabelSelect
                    label={tr('cfg_sync_pfields_ref', 'PRODUCT REFERENCE / SKU')}
                    value={value.reference || ''}
                    options={PRODUCT_REF_FIELDS}
                    onChange={(v) => onChange({ reference: v })}
                />
            </Box>
        </Box>
    </ConfigCard>
);
ProductFieldsCard.propTypes = { value: PropTypes.object, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// FIELD MAPPING — Language mapping
// ─────────────────────────────────────────────────────────────────────────
const LanguageMappingCard = ({ value = {}, onChange, theme, tr }) => (
    <ConfigCard
        title={tr('cfg_sync_lang_title', 'Language mapping')}
        description={tr('cfg_sync_lang_desc', 'Match each shop language to the corresponding language slot in your source data. The source system stores translations in numbered slots (Lang 1, Lang 2, etc.).')}
        theme={theme}
    >
        <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                {[
                    { code: 'nl', short: 'NL', label: tr('cfg_sync_lang_nl', 'DUTCH READS FROM') },
                    { code: 'en', short: 'GB', label: tr('cfg_sync_lang_en', 'ENGLISH READS FROM') },
                    { code: 'fr', short: 'FR', label: tr('cfg_sync_lang_fr', 'FRENCH READS FROM') },
                ].map(lang => (
                    <Box key={lang.code}>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, mb: 0.5, fontSize: '0.7rem' }}>
                            <Box component="span" sx={{ color: 'text.disabled', mr: 0.5 }}>{lang.short}</Box>
                            {lang.label}
                        </Typography>
                        <TextField
                            select size="small" fullWidth
                            value={value[lang.code] || ''}
                            onChange={(e) => onChange({ [lang.code]: e.target.value })}
                            sx={{ backgroundColor: '#fff', '& .MuiInputBase-input': { py: 0.75, fontSize: '0.85rem' } }}
                        >
                            {LANG_SLOTS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                    </Box>
                ))}
            </Box>
        </Box>
    </ConfigCard>
);
LanguageMappingCard.propTypes = { value: PropTypes.object, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// IMAGES — Image source types
// ─────────────────────────────────────────────────────────────────────────
const ImageSourceTypesCard = ({ value = {}, onChange, theme, tr }) => (
    <ConfigCard
        title={tr('cfg_sync_imgsrc_title', 'Image source types')}
        description={tr('cfg_sync_imgsrc_desc', 'Identify which file type prefix in the source system represents which product image.')}
        theme={theme}
    >
        <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <UpperLabelSelect
                    label={tr('cfg_sync_imgsrc_primary', 'PRIMARY IMAGE (MAIN PHOTO)')}
                    value={value.primary || ''}
                    options={IMAGE_TYPES}
                    onChange={(v) => onChange({ primary: v })}
                />
                <UpperLabelSelect
                    label={tr('cfg_sync_imgsrc_alt', 'ALTERNATIVE IMAGE (HOVER / SECONDARY)')}
                    value={value.alternative || ''}
                    options={IMAGE_TYPES}
                    onChange={(v) => onChange({ alternative: v })}
                />
            </Box>
        </Box>
    </ConfigCard>
);
ImageSourceTypesCard.propTypes = { value: PropTypes.object, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// IMAGES — Image processing
// ─────────────────────────────────────────────────────────────────────────
const ImageProcessingCard = ({ value = {}, onChange, theme, tr }) => {
    const v2 = value.useV2 !== false;
    const sizes = value.sizes || {};
    const setSize = (purpose, dim, n) => onChange({
        sizes: { ...sizes, [purpose]: { ...(sizes[purpose] || {}), [dim]: Math.max(1, parseInt(n) || 0) } },
    });

    const PURPOSES = [
        { key: 'thumb',     label: tr('cfg_sync_imgproc_thumb',      'Thumb'),
                            help:  tr('cfg_sync_imgproc_thumb_help', 'Small previews in lists and grids'),
                            defaults: { width: 200, height: 320 } },
        { key: 'preview',   label: tr('cfg_sync_imgproc_preview',     'Preview'),
                            help:  tr('cfg_sync_imgproc_preview_help','Medium-sized images on category pages'),
                            defaults: { width: 750, height: 1120 } },
        { key: 'processed', label: tr('cfg_sync_imgproc_processed',     'Processed'),
                            help:  tr('cfg_sync_imgproc_processed_help','Full-size product detail page images'),
                            defaults: { width: 800, height: 1300 } },
    ];

    return (
        <ConfigCard
            title={tr('cfg_sync_imgproc_title', 'Image processing')}
            description={tr('cfg_sync_imgproc_desc', 'When a new image is uploaded to the source, the system processes it into multiple sizes for the webshop.')}
            theme={theme}
        >
            <Alert severity="warning" icon={false} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
                <Typography variant="body2" sx={{ '& strong': { fontWeight: 700 } }}>
                    <Box component="strong">{tr('cfg_sync_imgproc_v2_rec_title', 'v2 processing recommended.')}</Box>{' '}
                    {tr('cfg_sync_imgproc_v2_rec_body', "The newer pipeline preserves aspect ratios and respects width/height limits independently, so images aren't forced into squares. Turn off only if you specifically need legacy behavior.")}
                </Typography>
            </Alert>

            <PdiRow
                label={tr('cfg_sync_imgproc_use_v2', 'Use v2 image processing')}
                help={tr('cfg_sync_imgproc_use_v2_help', 'Newer pipeline that respects aspect ratios and per-dimension limits')}
                control={
                    <Switch color="success" size="small"
                        checked={v2}
                        onChange={(e) => onChange({ useV2: e.target.checked })}
                    />
                }
            />

            {v2 && (
                <Box sx={{ pl: 2.5, borderLeft: '2px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                        {tr('cfg_sync_imgproc_max_sizes', 'Maximum sizes')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1, lineHeight: 1.4 }}>
                        {tr('cfg_sync_imgproc_max_sizes_help', 'The longest a width or height can be for each image purpose. Smaller files load faster but show less detail.')}
                    </Typography>

                    <Box sx={{ p: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 130px 130px',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 0.75,
                        }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                                {tr('cfg_sync_imgproc_purpose', 'PURPOSE')}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                                {tr('cfg_sync_imgproc_max_w', 'MAXIMUM WIDTH')}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                                {tr('cfg_sync_imgproc_max_h', 'MAXIMUM HEIGHT')}
                            </Typography>
                        </Box>
                        <Stack spacing={0.75}>
                            {PURPOSES.map(p => {
                                const cur = sizes[p.key] || p.defaults;
                                return (
                                    <Box key={p.key} sx={{ display: 'grid', gridTemplateColumns: '1fr 130px 130px', alignItems: 'center', gap: 1.5 }}>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.label}</Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>{p.help}</Typography>
                                        </Box>
                                        <TextField
                                            type="number" size="small"
                                            value={cur.width ?? ''}
                                            onChange={(e) => setSize(p.key, 'width', e.target.value)}
                                            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                            sx={{ backgroundColor: '#fff' }}
                                        />
                                        <TextField
                                            type="number" size="small"
                                            value={cur.height ?? ''}
                                            onChange={(e) => setSize(p.key, 'height', e.target.value)}
                                            InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                            sx={{ backgroundColor: '#fff' }}
                                        />
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>

                    <Box sx={{ mt: 1.5 }}>
                        <PdiRow
                            label={tr('cfg_sync_imgproc_bg_fill', 'Background fill color')}
                            help={tr('cfg_sync_imgproc_bg_fill_help', "Used to pad images that don't fit the target aspect ratio. White blends most cleanly with white-background product photos.")}
                            control={
                                <Box component="label" sx={{
                                    position: 'relative',
                                    display: 'block',
                                    width: 36, height: 36,
                                    borderRadius: 1,
                                    border: '1px solid rgba(0,0,0,0.18)',
                                    backgroundColor: value.backgroundFillColor || '#ffffff',
                                    cursor: 'pointer',
                                }}>
                                    <input
                                        type="color"
                                        value={value.backgroundFillColor || '#ffffff'}
                                        onChange={(e) => onChange({ backgroundFillColor: e.target.value })}
                                        style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                                    />
                                </Box>
                            }
                        />
                    </Box>
                </Box>
            )}
        </ConfigCard>
    );
};
ImageProcessingCard.propTypes = { value: PropTypes.object, onChange: PropTypes.func, theme: PropTypes.string, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// Helpers used inside the cards
// ─────────────────────────────────────────────────────────────────────────

// Small select with an UPPER-CASE label above (matches the schema's "PRODUCT
// NAME" / "PRIMARY IMAGE" boxed inputs).
const UpperLabelSelect = ({ label, value, options, onChange }) => (
    <Box>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'text.secondary', letterSpacing: 0.5, mb: 0.5, fontSize: '0.7rem' }}>
            {label}
        </Typography>
        <TextField
            select size="small" fullWidth
            value={value}
            onChange={(e) => onChange(e.target.value)}
            sx={{ backgroundColor: '#fff', '& .MuiInputBase-input': { py: 0.75, fontSize: '0.85rem' } }}
        >
            {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
    </Box>
);
UpperLabelSelect.propTypes = { label: PropTypes.string, value: PropTypes.string, options: PropTypes.array, onChange: PropTypes.func };

export default SyncSettingsTab;
